import {
  ApiListResponse,
  ProductCategoryDTO,
  ProductDTO,
  ProductOrderDetailDTO,
  ProductOrderSummaryDTO,
  ProductStatus,
} from "@gymflow/types";
import { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";

export type ProductApiType = ReturnType<typeof productApi>;

const productApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = ["name", "currency"];

  return {
    update(id: number, patchedFields: Partial<any>) {
      return axiosInstance.patch<ProductDTO>(
        `${clubPrefix}product/${id}`,
        trimFields(patchedFields, fieldsToTrim),
      );
    },
    find({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: {
      readonly extraParams?: {
        readonly unpaged?: boolean;
        readonly status?: ProductStatus;
        readonly id?: number[];
        readonly [k: string]: any;
      };
      readonly page?: number;
      readonly limit?: number;
      readonly sort?: {
        readonly field: string;
        readonly desc?: boolean;
      };
      readonly dateFrom?: string;
      readonly dateTo?: string;
      readonly unpaged?: boolean;
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }
      return axiosInstance.get<ApiListResponse<ProductDTO>>(
        `${clubPrefix}product`,
        config,
      );
    },
    findById(id: number) {
      return axiosInstance.get<ProductDTO>(`${clubPrefix}product/${id}`);
    },
    create(newResource: any) {
      return axiosInstance.post<ProductDTO>(`${clubPrefix}product`, {
        ...trimFields(newResource, fieldsToTrim),
        clubIdList: [clubId],
      });
    },
    addPicture({ id, picture }: { id: number; picture: Blob }) {
      const formData = new FormData();
      formData.append("file", picture);
      return axiosInstance.post<string>(
        `${clubPrefix}product/${id}/picture`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },

    findCategories({
      page = 0,
      limit = 10,
    }: {
      readonly page?: number;
      readonly limit?: number;
    }) {
      return axiosInstance.put<ApiListResponse<ProductCategoryDTO>>(
        `${clubPrefix}product/category/search`,
        {
          page,
          size: limit,
        },
      );
    },

    deactivate(id: number) {
      return axiosInstance.patch(`${clubPrefix}product/${id}/unavailable`);
    },

    activate(id: number) {
      return axiosInstance.patch(`${clubPrefix}product/${id}/available`);
    },

    summary({
      products,
      promotionCode,
      userMemberId,
    }: {
      products: ProductOrderDetailDTO[];
      promotionCode?: string;
      userMemberId: string;
    }) {
      return axiosInstance.post<ProductOrderSummaryDTO>(
        `${clubPrefix}product/order/summary`,
        {
          productOrderList: products,
          promotionCode,
          userMemberId,
        },
      );
    },
    delete(productId: number) {
      return axiosInstance.delete(`${clubPrefix}product/${productId}`);
    },
  };
};

export default productApi;
